import { useSyncExternalStore } from "react";
import { canonicalEditorImportProject, useUploadStore } from "../../store/uploadStore";

/** Presentation ownership only. Unity and the strict importer still decide completion. */
export type EditorImportRestoreOwner = {
  requestId: string;
  uploadItemId: string;
  project: string;
  targetSpaceIndex: number;
  remountTransactionId: string | null;
  projectLoadTransactionId: string | null;
  transactionId: string | null;
  executionId: string | null;
  runtime: HTMLIFrameElement | null;
};

let owner: EditorImportRestoreOwner | null = null;
const listeners = new Set<() => void>();
const publish = (next: EditorImportRestoreOwner | null) => {
  owner = next;
  listeners.forEach(listener => listener());
};
export const getEditorImportRestoreOwner = () => owner;
export const useEditorImportRestoreOwner = () => useSyncExternalStore(
  listener => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  getEditorImportRestoreOwner,
  () => null,
);

export function beginEditorImportRestoreOwner(requestId: string, uploadItemId: string, targetSpaceIndex: number | null): void {
  const item = useUploadStore.getState().items[uploadItemId];
  const project = canonicalEditorImportProject(item?.originViewrGcsPath);
  if (item?.flow !== "editor_object_import" || !item.editorImportAdmission || !project ||
      typeof targetSpaceIndex !== "number" || targetSpaceIndex !== item.targetSpaceIndex) return;
  if (owner?.requestId === requestId) return;
  publish({ requestId, uploadItemId, project, targetSpaceIndex, remountTransactionId: null, projectLoadTransactionId: null, transactionId: null, executionId: null, runtime: null });
}

export function bindEditorImportRestoreRemount(requestId: string | null, transactionId: string): void {
  if (!requestId || owner?.requestId !== requestId || owner.transactionId) return;
  if (owner.remountTransactionId && owner.remountTransactionId !== transactionId) return;
  publish({ ...owner, remountTransactionId: transactionId });
}

/** Revision restore loads the original project under its own project_load T; it belongs to the same R. */
export function bindEditorImportRestoreProjectLoad(requestId: string, transactionId: string): void {
  if (owner?.requestId !== requestId || owner.transactionId) return;
  publish({ ...owner, projectLoadTransactionId: transactionId });
}

export function bindEditorImportRestoreExecution(requestId: string, uploadItemId: string, executionId: string, transactionId: string): void {
  if (!owner || owner.requestId !== requestId || owner.uploadItemId !== uploadItemId) return;
  const item = useUploadStore.getState().items[uploadItemId];
  if (owner.project !== canonicalEditorImportProject(item.originViewrGcsPath) ||
      owner.targetSpaceIndex !== item.targetSpaceIndex || item.editorImportAdmission?.executionId !== executionId) {
    throw new Error("editor_import_original_target_changed");
  }
  publish({ ...owner, executionId, transactionId, runtime: document.getElementById("unity-iframe") as HTMLIFrameElement | null });
}

export function isEditorImportRestoreTransaction(transactionId: string | null | undefined): boolean {
  return Boolean(transactionId && owner &&
    (owner.remountTransactionId === transactionId || owner.projectLoadTransactionId === transactionId ||
      owner.transactionId === transactionId));
}

export function endEditorImportRestoreOwner(requestId: string): void {
  if (owner?.requestId === requestId) publish(null);
}
